/**
 * IciciFundDetails Component
 * ICICI-specific fund details display with proper formatting for ICICI Direct API response
 */

import React from "react";
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  IconButton,
  LinearProgress,
} from "@mui/material";
import {
  AccountBalance,
  TrendingUp,
  TrendingDown,
  Refresh,
  Info,
  Warning,
} from "@mui/icons-material";

// Headline currency figures: formatCurrency emits en-IN strings like "₹12,34,567.89",
// which have no break opportunities (commas/periods are not break points). At the
// default h4 size (2.125rem) that overflows the ~343px phone viewport and gets clipped
// by the Card's overflow:hidden. Shrink the figure on small screens, and allow an
// anywhere-break as a last resort so an extreme value wraps rather than truncates.
// Paired with the summary cards' xs=12 / sm=6 / lg=3 layout: four-across only starts
// at lg, where the column is wide enough for the full 2.125rem h4.
const figureSx = {
  fontSize: { xs: "1.5rem", sm: "1.75rem", lg: "2.125rem" },
  overflowWrap: "anywhere",
};

const IciciFundDetails = ({
  account, 
  funds, 
  lastUpdated, 
  onRefresh, 
  formatCurrency, 
  formatNumber 
}) => {
  // Extract ICICI fund data structure
  const fundData = funds?.funds_data?.Success || funds?.Success || {};
  
  // Calculate totals and utilization
  const calculateSegmentUtilization = (allocated, blocked) => {
    const allocatedNum = parseFloat(allocated) || 0;
    const blockedNum = parseFloat(blocked) || 0;
    
    if (allocatedNum === 0) return 0;
    return (blockedNum / allocatedNum) * 100;
  };

  // Get utilization color
  const getUtilizationColor = (percentage) => {
    if (percentage > 80) return "error";
    if (percentage > 60) return "warning";
    return "success";
  };

  // Get utilization badge variant (DS)
  const getUtilizationBadgeVariant = (percentage) => {
    if (percentage > 80) return "loss";
    if (percentage > 60) return "warn";
    return "profit";
  };

  // Segment data
  const segments = [
    {
      name: "Equity",
      allocated: fundData.allocated_equity,
      blocked: fundData.block_by_trade_equity,
      icon: TrendingUp,
      color: "primary"
    },
    {
      name: "F&O", 
      allocated: fundData.allocated_fno,
      blocked: fundData.block_by_trade_fno,
      icon: TrendingDown,
      color: "secondary"
    },
    {
      name: "Commodity",
      allocated: fundData.allocated_commodity, 
      blocked: fundData.block_by_trade_commodity,
      icon: AccountBalance,
      color: "warning"
    },
    {
      name: "Currency",
      allocated: fundData.allocated_currency,
      blocked: fundData.block_by_trade_currency, 
      icon: AccountBalance,
      color: "info"
    }
  ];

  // Calculate totals and utilization
  const totalBankBalance = parseFloat(fundData.total_bank_balance) || 0;
  const totalAllocated = segments.reduce((sum, segment) => 
    sum + (parseFloat(segment.allocated) || 0), 0
  );
  const totalBlocked = segments.reduce((sum, segment) => 
    sum + (parseFloat(segment.blocked) || 0), 0
  );
  const availableBalance = totalAllocated - totalBlocked;
  const overallUtilization = totalAllocated > 0 ? (totalBlocked / totalAllocated) * 100 : 0;

  return (
    <div className="bg-bg-2 rounded-xl shadow-lg border border-[var(--divider)] backdrop-blur-sm transition-all duration-300">
      <div className="p-4 sm:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="shrink-0 p-2 sm:p-3 bg-[var(--badge-info-bg)] rounded-lg">
              <AccountBalance className="text-[var(--badge-info-fg)]" sx={{ fontSize: { xs: 24, sm: 32 } }} />
            </div>
            <div className="min-w-0">
              <Typography
                variant="h5"
                className="text-fg-1 font-semibold tracking-tight"
                sx={{ fontSize: { xs: '1.125rem', sm: '1.5rem' } }}
              >
                ICICI Direct - Fund Details
              </Typography>
              <Typography
                variant="body2"
                className="text-fg-2 mt-0.5 min-w-0 break-all"
              >
                Bank Account: {fundData.bank_account || "N/A"}
              </Typography>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {lastUpdated && (
              <Typography
                variant="caption"
                className="text-fg-2 bg-bg-3/50 px-2 py-1 rounded-full whitespace-nowrap"
              >
                Last Updated: {lastUpdated.toLocaleTimeString()}
              </Typography>
            )}
            <IconButton
              onClick={onRefresh}
              size="small"
              className="text-fg-2 hover:text-accent transition-colors duration-200 bg-bg-3/50 hover:bg-[var(--badge-accent-bg)]"
              sx={{ minWidth: { xs: 44, sm: 0 }, minHeight: { xs: 44, sm: 0 } }}
            >
              <Refresh />
            </IconButton>
          </div>
        </div>

        <Divider className="border-[var(--divider)] mb-8" />

        {/* Summary Cards */}
        <Grid container spacing={{ xs: 2, md: 4 }} className="mb-8">
          <Grid item xs={12} sm={6} lg={3}>
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10 border-0 shadow-md hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden">
              <CardContent className="text-center p-4 sm:p-6">
                <Typography variant="h4" className="font-bold text-blue-600 dark:text-blue-400 mb-2 tracking-tight" sx={figureSx}>
                  {formatCurrency(totalBankBalance)}
                </Typography>
                <Typography variant="body2" className="text-fg-2 font-medium">
                  Total Bank Balance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={3}>
            <Card className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/10 border-0 shadow-md hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden">
              <CardContent className="text-center p-4 sm:p-6">
                <Typography variant="h4" className="font-bold text-green-600 dark:text-green-400 mb-2 tracking-tight" sx={figureSx}>
                  {formatCurrency(totalAllocated)}
                </Typography>
                <Typography variant="body2" className="text-fg-2 font-medium">
                  Total Allocated
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={3}>
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-900/20 dark:to-orange-800/10 border-0 shadow-md hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden">
              <CardContent className="text-center p-4 sm:p-6">
                <Typography variant="h4" className="font-bold text-orange-600 dark:text-orange-400 mb-2 tracking-tight" sx={figureSx}>
                  {formatCurrency(totalBlocked)}
                </Typography>
                <Typography variant="body2" className="text-fg-2 font-medium">
                  Total Blocked
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={3}>
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-800/10 border-0 shadow-md hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden">
              <CardContent className="text-center p-4 sm:p-6">
                <Typography variant="h4" className="font-bold text-emerald-600 dark:text-emerald-400 mb-2 tracking-tight" sx={figureSx}>
                  {formatCurrency(availableBalance)}
                </Typography>
                <Typography variant="body2" className="text-fg-2 font-medium">
                  Available Balance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Segment-wise Breakdown */}
        <Typography variant="h6" className="text-fg-1 font-semibold mb-6">
          Segment-wise Fund Allocation
        </Typography>
        
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {segments.map((segment) => {
            const allocated = parseFloat(segment.allocated) || 0;
            const blocked = parseFloat(segment.blocked) || 0;
            const available = allocated - blocked;
            const utilization = calculateSegmentUtilization(allocated, blocked);
            const IconComponent = segment.icon;
            
            return (
              <Grid item xs={12} md={6} key={segment.name}>
                <Card className="bg-bg-2 border border-[var(--divider)] shadow-md hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`shrink-0 p-2 rounded-lg ${
                          segment.color === 'primary' ? 'bg-blue-100 dark:bg-blue-900/30' :
                          segment.color === 'secondary' ? 'bg-purple-100 dark:bg-purple-900/30' :
                          segment.color === 'warning' ? 'bg-orange-100 dark:bg-orange-900/30' :
                          'bg-cyan-100 dark:bg-cyan-900/30'
                        }`}>
                          <IconComponent className={`text-2xl ${
                            segment.color === 'primary' ? 'text-blue-600 dark:text-blue-400' :
                            segment.color === 'secondary' ? 'text-purple-600 dark:text-purple-400' :
                            segment.color === 'warning' ? 'text-orange-600 dark:text-orange-400' :
                            'text-cyan-600 dark:text-cyan-400'
                          }`} />
                        </div>
                        <Typography variant="h6" className="text-fg-1 font-semibold truncate">
                          {segment.name}
                        </Typography>
                      </div>
                      <span className={`ds-badge sm shrink-0 whitespace-nowrap ${getUtilizationBadgeVariant(utilization)}`}>
                        {`${utilization.toFixed(1)}% Used`}
                      </span>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between items-center gap-3">
                        <Typography variant="body2" className="text-fg-2 shrink-0">
                          Allocated
                        </Typography>
                        <Typography variant="body2" className="font-semibold text-fg-1 text-right min-w-0 break-words">
                          {formatCurrency(allocated)}
                        </Typography>
                      </div>
                      
                      <div className="flex justify-between items-center gap-3">
                        <Typography variant="body2" className="text-fg-2 shrink-0">
                          Blocked
                        </Typography>
                        <Typography variant="body2" className="font-semibold text-[var(--badge-warn-fg)] text-right min-w-0 break-words">
                          {formatCurrency(blocked)}
                        </Typography>
                      </div>
                      
                      <div className="flex justify-between items-center gap-3">
                        <Typography variant="body2" className="text-fg-2 shrink-0">
                          Available
                        </Typography>
                        <Typography variant="body2" className="font-semibold text-profit text-right min-w-0 break-words">
                          {formatCurrency(available)}
                        </Typography>
                      </div>

                      {/* Utilization bar */}
                      <Box className="mt-3">
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(utilization, 100)}
                          color={getUtilizationColor(utilization)}
                          className="h-2 rounded-full"
                          sx={{
                            // Theme-aware track: a 5%-black track is invisible against
                            // --bg-2 (#1a1a1a) in dark theme, which hides the bar's baseline.
                            backgroundColor: 'var(--bg-3)',
                            '.MuiLinearProgress-bar': {
                              transition: 'transform 0.4s ease-in-out',
                            },
                          }}
                        />
                      </Box>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Additional Fund Information */}
        {(fundData.unallocated_balance || fundData.block_by_trade_balance) && (
          <div className="mt-8">
            <Typography variant="h6" className="text-fg-1 font-semibold mb-6">
              Additional Fund Details
            </Typography>
            
            <Grid container spacing={{ xs: 2, md: 4 }}>
              {fundData.unallocated_balance && (
                <Grid item xs={12} md={6}>
                  <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-900/20 dark:to-purple-800/10 border-0 shadow-md hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden">
                    <CardContent className="text-center p-4 sm:p-6">
                      <Typography variant="h5" className="font-bold text-purple-600 dark:text-purple-400 mb-2 tracking-tight" sx={{ overflowWrap: 'anywhere' }}>
                        {formatCurrency(parseFloat(fundData.unallocated_balance))}
                      </Typography>
                      <Typography variant="body2" className="text-fg-2 font-medium">
                        Unallocated Balance
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              
              {fundData.block_by_trade_balance && (
                <Grid item xs={12} md={6}>
                  <Card className="bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-900/20 dark:to-red-800/10 border-0 shadow-md hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden">
                    <CardContent className="text-center p-4 sm:p-6">
                      <Typography variant="h5" className="font-bold text-red-600 dark:text-red-400 mb-2 tracking-tight" sx={{ overflowWrap: 'anywhere' }}>
                        {formatCurrency(parseFloat(fundData.block_by_trade_balance))}
                      </Typography>
                      <Typography variant="body2" className="text-fg-2 font-medium">
                        Total Trade Block
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </div>
        )}

        {/* Status Information */}
        <div className="mt-8">
          <Alert
            severity="info"
            icon={<Info className="text-[var(--badge-info-fg)]" />}
            className="mb-3 rounded-xl"
            sx={{
              backgroundColor: 'var(--badge-info-bg)',
              color: 'var(--badge-info-fg)',
              border: '1px solid var(--divider)',
              '& .MuiAlert-icon': { color: 'var(--badge-info-fg)' },
            }}
          >
            Fund data is retrieved directly from ICICI Direct API. All amounts are in Indian Rupees (₹).
          </Alert>
          
          {overallUtilization > 80 && (
            <Alert
              severity="warning"
              icon={<Warning className="text-[var(--badge-warn-fg)]" />}
              className="mt-3 rounded-xl"
              sx={{
                backgroundColor: 'var(--badge-warn-bg)',
                color: 'var(--badge-warn-fg)',
                border: '1px solid var(--divider)',
                '& .MuiAlert-icon': { color: 'var(--badge-warn-fg)' },
              }}
            >
              High fund utilization detected. Consider adding more funds or reducing positions.
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
};

export default IciciFundDetails;
